import React, { useState, useRef } from "react";
import { ClickAwayListener } from "@mui/material";
import {
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  MenuItem,
  Menu,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoIcon from "../Icons/BloomPeopleLogo.png";
import { hasPermission } from "../utils/auth";
import HomeIcon from "@mui/icons-material/HomeOutlined";
import HomeHoverIcon from "@mui/icons-material/Home";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PersonIcon from "@mui/icons-material/Person";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import GroupIcon from "@mui/icons-material/Group";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import BadgeIcon from "@mui/icons-material/Badge";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import DevicesIcon from "@mui/icons-material/Devices";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import SecurityIcon from "@mui/icons-material/Security";
import GroupsIcon from "@mui/icons-material/Groups";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined"; // Employees
import WorkOutlineIcon from "@mui/icons-material/WorkOutline"; // Employee Projects
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined"; // Timesheet
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined"; // Timesheet Approval
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined"; // Leave Request
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined"; // Leave Approval
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined"; // Users
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined"; // Groups
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined"; // Screens
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined"; // Clients
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined"; // Client Projects
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined"; // Permissions
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";

const Sidebar = ({ mobileOpen, onToggle, isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openSubmenu, setOpenSubmenu] = useState("");
  const submenuRef = useRef(null);
  const [submenuPosition, setSubmenuPosition] = useState({
    top: 100,
    left: 100,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const openAccountMenu = Boolean(anchorEl);
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const handleAccountClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleAccountClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = () => {
    // Fire & forget (no await)
    fetch(`${BASE_URL}/api/logout`, {
      method: "POST",
      credentials: "include",
    }).catch((err) => console.error("Logout failed:", err));

    // Immediately redirect
    navigate("/login", { replace: true });
  };
  // --- Layout constants so it's easy to tweak ---
  const APPBAR_HEIGHT = 64; // height for AppBar/Toolbar
  const LOGO_HEIGHT = 40; // logo height (must be < APPBAR_HEIGHT)
  const SIDEBAR_WIDTH = 100; // left narrow bar width
  const ICON_PIXEL_SIZE = 26; // consistent icon size for sidebar
  // ------------------------------------------------

  const handleNavigation = (path) => {
    navigate(path);
    setOpenSubmenu("");
    if (isMobile) onToggle(); // Close drawer after navigation
  };
  // helper to standardize icons (works for MUI icons)
  const renderIcon = (icon) => {
    if (!React.isValidElement(icon)) return icon;
    return React.cloneElement(icon, {
      // use style so it always sets pixel size
      style: { fontSize: ICON_PIXEL_SIZE, display: "block", lineHeight: 1 },
    });
  };

  const mainMenus = [
    {
      label: "Dashboard",
      icon: <HomeIcon />,
      path: "/dashboard",
      permission: { screen: "Dashboard", action: "View" },
    },
    {
      label: "Organization",
      icon: <CorporateFareOutlinedIcon />,
      path: "/organization",
      permission: { screen: "Organization", action: "View" },
    },
    {
      label: "Department",
      icon: <HomeIcon />,
      path: "/Department",
      permission: { screen: "Department", action: "View" },
    },
    {
      label: "Clients",
      icon: <BusinessOutlinedIcon />,
      hoverIcon: <GroupIcon />,
      path: "/Clients",
      permission: { screen: "Clients", action: "View" },
      submenus: [
        {
          label: "Clients",
          path: "/clients",
          icon: <BusinessOutlinedIcon />,
          permission: { screen: "Clients", action: "View" },
        },
        {
          label: "Client Projects",
          path: "/clientprojects",
          icon: <FolderOpenOutlinedIcon />,
          permission: { screen: "Client Projects", action: "View" },
        },
      ],
    },
    {
      label: "Assets",
      icon: <DevicesOtherOutlinedIcon />,
      hoverIcon: <DevicesIcon />,
      path: "/assets",
      permission: { screen: "Assets", action: "View" },
    },
    {
      label: "Employees",
      icon: <PeopleAltOutlinedIcon />,
      hoverIcon: <BadgeIcon />,
      path: "/employees",
      permission: { screen: "Employees", action: "View" },
      submenus: [
        // { label: "Employees", path: "/employees", icon: <InsertDriveFileOutlinedIcon /> },
        {
          label: "Employee",
          path: "/employeedetails",
          icon: <PeopleAltOutlinedIcon />,
          permission: { screen: "Employees", action: "View" },
        },
        {
          label: "Employee Projects",
          path: "/employeeprojects",
          icon: <WorkOutlineIcon />,
          permission: { screen: "Employee Projects", action: "View" },
        },
        {
          label: "Timesheet",
          path: "/timesheet",
          icon: <AccessTimeOutlinedIcon />,
          permission: { screen: "Timesheet", action: "View" },
        },
        {
          label: "Timesheet Tasks",
          path: "/timesheettracker",
          icon: <FactCheckOutlinedIcon />,
          permission: { screen: "Timesheet Approval", action: "View" },
        },
        {
          label: "Payslip",
          path: "/payslip",
          icon: <FolderSharedOutlinedIcon />,
          permission: { screen: "Payslip", action: "View" },
        },
        {
          label: "Leave Request",
          path: "/leaverequest",
          icon: <EventNoteOutlinedIcon />,
          permission: { screen: "Leave Request", action: "View" },
        },

        {
          label: "Leave Approval",
          path: "/leaveapproval",
          icon: <AssignmentTurnedInOutlinedIcon />,
          permission: { screen: "Leave Approval", action: "View" },
        },
        {
          label: "Employee Salaries",
          path: "/employeesalaries",
          icon: <FolderSharedOutlinedIcon />,
          permission: { screen: "Employee Salaries", action: "View" },
        },
        {
          label: "Employee Assets",
          path: "/employeeassets",
          icon: <Inventory2OutlinedIcon />,
          permission: { screen: "Employee Assets", action: "View" },
        },
        {
          label: "Employee Attendance",
          path: "/employeeattendance",
          icon: <EventAvailableOutlinedIcon />,
        },
      ],
    },
    {
      label: "User",
      icon: <PersonOutlineIcon />,
      hoverIcon: <PersonIcon />,
      path: "/user",
      permission: { screen: "User", action: "View" },
      submenus: [
        {
          label: "Users",
          path: "/users",
          icon: <ManageAccountsOutlinedIcon />,
          permission: { screen: "Users", action: "View" },
        },
        {
          label: "Roles",
          path: "/roles",
          icon: <SecurityIcon />,
          permission: { screen: "Roles", action: "View" },
        },
        {
          label: "User Roles",
          path: "/userroles",
          icon: <BadgeIcon />,
          permission: { screen: "User Roles", action: "View" },
        },
        {
          label: "Screens",
          path: "/screens",
          icon: <DashboardOutlinedIcon />,
          permission: { screen: "Screens", action: "View" },
        },
        {
          label: "Groups",
          path: "/groups",
          icon: <GroupsOutlinedIcon />,
          permission: { screen: "Groups", action: "View" },
        },
        {
          label: "Permissions",
          path: "/permissions",
          icon: <VpnKeyIcon />,
          permission: { screen: "Permissions", action: "View" },
        },
      ],
    },
  ];

  const filteredMenus = mainMenus
    .map((menu) => {
      const filteredSubmenus =
        menu.submenus?.filter((sub) => {
          const result =
            !sub.permission ||
            hasPermission(sub.permission.screen, sub.permission.action);
          return result;
        }) || [];

      const hasMenuPermission =
        !menu.permission ||
        hasPermission(menu.permission.screen, menu.permission.action);

      if (menu.submenus) {
        if (filteredSubmenus.length > 0) {
          return { ...menu, submenus: filteredSubmenus };
        } else {
          return null;
        }
      }

      if (hasMenuPermission) {
        return menu;
      }

      return null;
    })
    .filter(Boolean);

  const content = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: "100%",
        backgroundColor: "#f4f4f4",
        display: "flex",
        flexDirection: "column",
        paddingTop: 2,
      }}
    >
      {filteredMenus.map((menu) => {
        const isActive =
          openSubmenu === menu.label ||
          location.pathname === menu.path ||
          (menu.submenus &&
            menu.submenus.some((sub) =>
              location.pathname.startsWith(sub.path)
            ));
        const displayedIcon =
          isActive && menu.hoverIcon ? menu.hoverIcon : menu.icon;
        return (
          <Tooltip title={menu.label} placement="right" key={menu.label}>
            <Box
              onClick={(e) => {
                if (menu.submenus) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setOpenSubmenu((prev) =>
                    prev === menu.label ? "" : menu.label
                  );
                  setSubmenuPosition({
                    top: rect.top + rect.height / 2 - 75,

                    left: rect.right + 10,
                  });
                } else {
                  setOpenSubmenu("");
                  handleNavigation(menu.path);
                }
              }}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
                padding: 1,
                borderRadius: 2,
                color: isActive ? "#1976d2" : "#333",
                backgroundColor: isActive ? "#e0f0ff" : "transparent",
                transition: "background 0.2s ease-in-out",
                height: 72, // fixed height
                minHeight: 72, // consistent vertical spacing
                boxSizing: "border-box", // include padding in height
              }}
            >
              {/* standardized icon */}
              <Box sx={{ lineHeight: 1 }}>{renderIcon(displayedIcon)}</Box>

              <Typography
                variant="caption"
                sx={{
                  fontSize: 11,
                  lineHeight: 1.1,
                  mt: 0.5,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                {menu.label}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
  const submenuItems =
    filteredMenus.find((menu) => menu.label === openSubmenu)?.submenus || [];
  const columnCount = Math.min(3, submenuItems.length);
  return (
    <>
      <AppBar
        position="fixed"
        sx={{ backgroundColor: "#fff", color: "#333", zIndex: 1201 }}
      >
        <Toolbar
          sx={{
            minHeight: APPBAR_HEIGHT,
            height: APPBAR_HEIGHT,
            alignItems: "center",
          }}
        >
          <IconButton
            color="inherit"
            edge="start"
            onClick={onToggle}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            component="img"
            src={LogoIcon}
            alt="Logo"
            sx={{ height: 120, width: "auto", mr: 0, display: "block" }}
          />
          <Box flexGrow={1} />
          <IconButton color="inherit" onClick={handleAccountClick}>
            <AccountCircleIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={openAccountMenu}
        onClose={handleAccountClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={() => {
            handleAccountClose();
            navigate("/profile");
          }}
        >
          Profile
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleAccountClose();
            handleLogout();
          }}
          style={{ color: "red" }}
        >
          Logout
        </MenuItem>
      </Menu>

      {/* Drawer on mobile */}
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={mobileOpen}
        onClose={onToggle}
        sx={{
          "& .MuiDrawer-paper": {
            width: 100,
            top: APPBAR_HEIGHT, // match the toolbar height
            boxSizing: "border-box",
          },
        }}
      >
        {content}
      </Drawer>

      {/* Submenu (same for both) */}
      {openSubmenu && (
        <ClickAwayListener onClickAway={() => setOpenSubmenu("")}>
          <Box
            ref={submenuRef}
            sx={{
              position: "fixed",
              top: submenuPosition.top,
              left: submenuPosition.left,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              boxShadow: "2px 0 5px rgba(0,0,0,0.1)",
              borderRadius: "12px",
              padding: "6px",
              display: "grid",
              gridTemplateColumns: `repeat(${Math.min(
                3,
                submenuItems.length
              )}, 70px)`,
              gap: "8px",
              zIndex: 1300,
              // fontFamily: "Poppins, sans-serif",
              transition: "all 0.2s ease-in-out",
            }}
          >

            {submenuItems.map((submenu) => (
              <Box
                key={submenu.label}
                onClick={() => handleNavigation(submenu.path)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  padding: "6px 4px",
                  borderRadius: 2,
                  textAlign: "center",
                  transition: "all 0.15s",
                  "&:hover": {
                    backgroundColor: "#f0f8ff",
                    transform: "translateY(-2px)",
                  },
                  minWidth: 70,
                }}
              >
                {/* Standardize icon size */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 36,
                  }}
                >
                  {React.cloneElement(submenu.icon, { fontSize: "medium" })}
                </Box>
                {/* Standardize label font */}
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 11,
                    lineHeight: 1.2,
                    mt: 0.5,
                    textAlign: "center",
                  }}
                >
                  {submenu.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </ClickAwayListener>
      )}
    </>
  );
};

export default Sidebar;
